import { ChevronRight, CircleCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { Button } from "../../../components/ui/button";
import { packageNames } from "../../../handlers/utilityFunctions";
import { base64ToAscii } from "../../../handlers/valueTransformation";
import { ResumeDataService } from "../../../services/create-resume/resume-data";
import type { PaymentDetails } from "./Checkout";
import Checkout from "./Checkout";
import PaymentComplete from "./PaymentComplete";
import SubscriptionOptions from "./SubscriptionOptions";

function PaymentFlow() {
	const location = useLocation();
	const [currentStep, setCurrentStep] = useState(1);
	const [usePoints, setUsePoints] = useState(false);

	const [paymentLink, setPaymentLink] = useState("");
	const [orderID, setOrderID] = useState("");
	const [orderData, setOrderData] = useState<{ orderID: string; paymentLink: string } | null>(null);
	const [loading, setLoading] = useState(false);

	const [searchParams] = useSearchParams();

	const URLOrderID = searchParams.get("order");

	useEffect(() => {
		if (URLOrderID) {
			const storedData = localStorage.getItem("orderID");
			if (storedData) {
				setOrderData(JSON.parse(storedData));
			}
			setOrderID(URLOrderID);
			setCurrentStep(4);
		}
	}, [URLOrderID]);

	const [selectedPackage, setSelectedPackage] = useState("");

	const [paymentDetails, setPaymentDetails] = useState({
		fullName: "",
		phoneNumber: "",
		amount: 0,
		packageDetail: "",
		emailAddress: "",
	});

	function getAmount() {
		const dprozPackage = packageNames[selectedPackage];
		setPaymentDetails((prev) => ({
			...prev,
			amount: dprozPackage["originalAmount"],
			packageDetail: dprozPackage["packageDetail"],
		}));
	}

	useEffect(() => {
		if (selectedPackage) {
			setCurrentStep(2);
			getAmount();
		}
	}, [selectedPackage]);

	useEffect(() => {
		if (location.state) {
			const amountString = location.state.amount;
			const amount = amountString.match(/(\d[\d,]*)/)?.[0].replace(/,/g, "");
			const days = location.state.selectedPackage === "GOLD" ? 60 : 365;
			setCurrentStep(2);
			setPaymentDetails((prev) => ({
				...prev,
				amount: amount,
				packageDetail: `Upgrade to Dproz ${location.state.selectedPackage} package. ${days} days full-access`,
			}));
		}
	}, [location]);

	const resumeDataService = new ResumeDataService();

	function restoreOrderID() {
		if (!URLOrderID) {
			const storedOrderID = localStorage.getItem("orderID");
			if (storedOrderID) {
				const { orderID, paymentLink } = JSON.parse(storedOrderID);
				setOrderID(orderID);
				setPaymentLink(base64ToAscii(paymentLink));
				setCurrentStep(3);
			}
		}
	}

	useEffect(() => {
		restoreOrderID();
	}, []);

	function onSuccess(response: { data: { paymentLink: string; _id: string } }) {
		setLoading(false);
		const { paymentLink, _id } = response.data;

		setOrderID(_id);
		localStorage.setItem(
			"orderID",
			JSON.stringify({
				orderID: _id,
				paymentLink: paymentLink,
			}),
		);

		const link = base64ToAscii(paymentLink);
		setPaymentLink(link);
		setCurrentStep(3);
	}

	const [errors, setErrors] = useState<string[]>([]);

	function onError(error: { message: string | string[] }) {
		setLoading(false);
		const newError = error.message;
		Array.isArray(newError) ? setErrors(newError) : setErrors([newError]);
	}

	async function checkout(paymentDetails: PaymentDetails) {
		setLoading(true);
		await resumeDataService.createPaymentOrder(paymentDetails, onSuccess, onError);
	}

	const benefits = [
		"Download and share your resume.",
		"Easily duplicate your resume to tailor it to the job you are applying for",
		"Get Systematic Completeness checks as you are building your resume.",
	];

	return (
		<div className="mt-8">
			<div className="rounded-lg bg-white py-6">
				{currentStep === 1 && (
					<SubscriptionOptions
						setSelectedPackage={setSelectedPackage}
						usePoints={usePoints}
						setUsePoints={setUsePoints}
					/>
				)}
				{currentStep === 2 && (
					<Checkout
						setCurrentStep={setCurrentStep}
						checkout={checkout}
						setPaymentDetails={setPaymentDetails}
						paymentDetails={paymentDetails}
						loading={loading}
						serverErrors={errors}
						selectedPackage={selectedPackage}
					/>
				)}
				{currentStep === 3 && (
					<div className="space-y-4 px-4">
						<h3 className="text-lg font-semibold">Complete Payment to Activate Subscription</h3>

						<a
							href={paymentLink}
							target="_blank"
							rel="noopener noreferrer"
							className="flex items-center justify-between rounded-lg border border-gray-300 px-4 py-3 text-gray-900 no-underline transition-colors hover:bg-gray-50"
						>
							<div className="w-3/4">
								<p className="mb-1 font-semibold">Complete Payment</p>
								<p className="text-sm text-gray-600">
									When you press &quot;Complete Payment&quot; you will be redirected to a payment gateway to complete
									your payment.
								</p>
							</div>
							<ChevronRight className="h-5 w-5 text-gray-400" />
						</a>

						<div className="flex items-center justify-between rounded-lg border border-gray-300 px-4 py-3">
							<div className="w-3/4">
								<p className="mb-1 font-semibold">Activate Subscription</p>
								<p className="text-sm text-gray-600">
									Once you have completed your payment, you will be redirected back to this page to verify your
									subscription.
								</p>
							</div>
							<ChevronRight className="h-5 w-5 text-gray-400" />
						</div>

						<div>
							<p className="font-semibold">When you activate your subscription you can:</p>
							<ul className="mt-3 space-y-2">
								{benefits.map((benefit) => (
									<li key={benefit}>
										<div className="inline-flex items-center gap-2 rounded bg-gray-100 px-3 py-2 text-sm">
											<CircleCheck className="h-4 w-4 shrink-0 text-green-600" />
											<span>{benefit}</span>
										</div>
									</li>
								))}
							</ul>
						</div>

						<div className="flex justify-end pt-2">
							<Button asChild>
								<a href={paymentLink} target="_blank" rel="noopener noreferrer">
									Complete Payment
								</a>
							</Button>
						</div>
					</div>
				)}
				{currentStep >= 4 && (
					<PaymentComplete
						orderID={orderID}
						loading={loading}
						setLoading={setLoading}
						orderData={orderData}
						setOrderData={setOrderData}
						setCurrentStep={setCurrentStep}
					/>
				)}
			</div>
		</div>
	);
}

export default PaymentFlow;
